import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import ModalStandard from "src/common/components/Modal/ModalStandard";
import { useAppDispatch } from "src/common/state/hooks";
import { setAlert } from "src/features/alert/state/alertsSlice";
import type { RagAgent, RagDocument } from "../api/ragApi";
import { assignDocumentToAgent, fetchOrgDocuments } from "../api/ragApi";
import { useAgentFormSubmit } from "../hooks/useAgentFormSubmit";
import { refetchAgentsThunk } from "../state/ragSlice";
import type { AgentFormData } from "./AgentConfigFields";
import AgentConfigFields from "./AgentConfigFields";
import DocumentLibraryList from "./DocumentLibraryList";

const defaultValues: AgentFormData = {
  name: "",
  description: "",
  system_prompt: "",
  llm_model: "mistral-large-latest",
  temperature: 0.3,
  icon: "default",
  figure_vision_enabled: true,
};

interface CreateAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (agent: RagAgent) => void;
}

const CreateAgentModal = ({ isOpen, onClose, onCreated }: CreateAgentModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { submitting, createAgent } = useAgentFormSubmit();
  const [view, setView] = useState<"form" | "documents">("form");
  const [selectedDocIds, setSelectedDocIds] = useState<number[]>([]);
  const [selectionOnEntry, setSelectionOnEntry] = useState<number[]>([]);
  const [orgDocs, setOrgDocs] = useState<RagDocument[] | null>(null);
  const [docsLoading, setDocsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<AgentFormData>({ defaultValues });

  const handleClose = () => {
    reset(defaultValues);
    setView("form");
    setSelectedDocIds([]);
    setOrgDocs(null);
    onClose();
  };

  const openDocuments = () => {
    setSelectionOnEntry(selectedDocIds);
    setView("documents");
    if (orgDocs !== null) return;
    setDocsLoading(true);
    fetchOrgDocuments()
      .then((docs) => setOrgDocs(docs))
      .catch((err) => {
        console.error("Failed to load org documents:", err);
        setOrgDocs([]);
      })
      .finally(() => setDocsLoading(false));
  };

  const toggleDocument = (docId: number) => {
    setSelectedDocIds((ids) =>
      ids.includes(docId) ? ids.filter((id) => id !== docId) : [...ids, docId],
    );
  };

  const onSubmit = async (data: AgentFormData) => {
    const agent = await createAgent(data);
    if (!agent) return;
    if (selectedDocIds.length > 0) {
      const results = await Promise.allSettled(
        selectedDocIds.map((docId) => assignDocumentToAgent(docId, agent.id)),
      );
      const failedCount = results.filter((r) => r.status === "rejected").length;
      if (failedCount > 0) {
        dispatch(
          setAlert({
            type: "warning",
            msg: t("pages.agents.createAgentModal.someDocumentsFailed", { count: failedCount }),
          }),
        );
      }
      await dispatch(refetchAgentsThunk());
    }
    onCreated(agent);
    handleClose();
  };

  return (
    <ModalStandard
      id="create-rag-agent"
      fullScreen={false}
      controlledMode={{ isOpen }}
      showCloseButton
      onClose={handleClose}
      minimize
      className="w-full max-w-2xl h-fit max-h-[calc(100vh-2rem)]"
    >
      {view === "form" ? (
        <form
          onSubmit={(event) => {
            event.stopPropagation();
            void handleSubmit(onSubmit)(event);
          }}
          className="space-y-5"
        >
          <h3 className="font-semibold text-base text-gray-900 pr-6">
            {t("pages.agents.createAgentModal.title")}
          </h3>
          <AgentConfigFields register={register} errors={errors} control={control} />
          <div className="flex flex-col sm:flex-row items-center gap-2 pt-4 border-t border-gray-300">
            <div className="sm:w-1/3 text-sm font-medium text-gray-700">
              {t("pages.agents.createAgentModal.documents")}
            </div>
            <div className="flex grow justify-end items-center gap-3">
              <span className="text-sm text-gray-500">
                {t("pages.agents.createAgentModal.documentsSelected", {
                  count: selectedDocIds.length,
                })}
              </span>
              <button
                type="button"
                onClick={openDocuments}
                className="btn-alternative-outline text-sm"
              >
                {t("pages.agents.createAgentModal.chooseDocuments")}
              </button>
            </div>
          </div>
          <div className="flex justify-end items-center gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={handleClose} className="btn-alternative-outline text-sm">
              {t("general.cancel")}
            </button>
            <button type="submit" disabled={submitting} className="btn-primary-fill text-sm">
              {submitting ? "..." : t("pages.agents.createAgent")}
            </button>
          </div>
        </form>
      ) : (
        <div>
          <h3 className="font-semibold text-base text-gray-900 mb-3 pr-6">
            {t("pages.agents.documentLibraryPicker")}
          </h3>
          <DocumentLibraryList
            docs={orgDocs ?? []}
            loading={docsLoading}
            mode={{ kind: "select", selectedIds: selectedDocIds, onToggle: toggleDocument }}
          />
          <div className="flex justify-end items-center gap-3 mt-4 pt-3 border-t border-gray-200">
            <button
              type="button"
              onClick={() => {
                setSelectedDocIds(selectionOnEntry);
                setView("form");
              }}
              className="btn-alternative-outline text-sm"
            >
              {t("general.back")}
            </button>
            <button
              type="button"
              onClick={() => setView("form")}
              className="btn-primary-fill text-sm"
            >
              {t("general.confirm")}
            </button>
          </div>
        </div>
      )}
    </ModalStandard>
  );
};

export default CreateAgentModal;
